import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import config from '../config';
import { getAuthHeader } from '../services/auth_helper';

const inputClassName =
  'w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 shadow-sm focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-200';
const labelClassName = 'text-sm font-medium text-slate-700';
const helperClassName = 'text-xs text-slate-500';
const buttonBaseClass =
  'inline-flex items-center justify-center rounded-lg px-4 py-2 text-sm font-semibold text-white shadow-sm transition disabled:cursor-not-allowed disabled:opacity-60';

const AdminPanel = () => {
  const user = useSelector((state) => state.userReducer.user);
  const isAdmin = user?.role === 'admin';
  const [schools, setSchools] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [notice, setNotice] = useState(null);
  const [loading, setLoading] = useState(false);
  const [teacherSearch, setTeacherSearch] = useState('');
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [resetModalOpen, setResetModalOpen] = useState(false);
  const [resetUserId, setResetUserId] = useState('');
  const [resetPassword, setResetPassword] = useState('');
  const [resetPasswordConfirm, setResetPasswordConfirm] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [teacherPage, setTeacherPage] = useState(1);
  const [teacherLimit, setTeacherLimit] = useState(25);
  const [teacherTotal, setTeacherTotal] = useState(0);
  const [teacherSortBy, setTeacherSortBy] = useState('name');
  const [teacherSortOrder, setTeacherSortOrder] = useState('asc');

  const [createSchool, setCreateSchool] = useState({
    name: '',
    code: '',
    school_emails: '',
    database_name: '',
    user_database_name: ''
  });
  const [editSchoolId, setEditSchoolId] = useState('');
  const [editSchool, setEditSchool] = useState({
    name: '',
    code: '',
    school_emails: ''
  });
  const [branchSchoolId, setBranchSchoolId] = useState('');
  const [branchForm, setBranchForm] = useState({ name: '', code: '' });
  const [assignTeacherId, setAssignTeacherId] = useState('');
  const [assignSchoolId, setAssignSchoolId] = useState('');
  const [assignBranchCode, setAssignBranchCode] = useState('');
  const [newUser, setNewUser] = useState({
    email: '',
    password: '',
    confirm_password: '',
    name: '',
    role: 'teacher',
    school_id: '',
    branch_code: ''
  });
  const [newUserSummary, setNewUserSummary] = useState(null);
  const [copyNotice, setCopyNotice] = useState('');

  const apiRequest = async (path, options = {}) => {
    const headers = getAuthHeader();
    if (!headers) {
      setNotice({ type: 'error', message: 'Please login again.' });
      return null;
    }
    const response = await fetch(`${config.URI}${path}`, {
      ...options,
      headers: {
        ...headers,
        ...(options.headers || {})
      }
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      setNotice({ type: 'error', message: data?.error || data?.message || 'Request failed.' });
      return null;
    }
    return data;
  };

  const loadSchools = async () => {
    const data = await apiRequest('/api/schools');
    if (data) {
      setSchools(Array.isArray(data) ? data : []);
    }
  };

  const loadTeachers = async ({
    page = teacherPage,
    limit = teacherLimit,
    sortBy = teacherSortBy,
    sortOrder = teacherSortOrder,
    search = teacherSearch
  } = {}) => {
    const params = new URLSearchParams();
    params.set('page', String(page));
    params.set('limit', String(limit));
    params.set('sort_by', sortBy);
    params.set('sort_order', sortOrder);
    const trimmedSearch = String(search || '').trim();
    if (trimmedSearch) {
      params.set('search', trimmedSearch);
    }
    const data = await apiRequest(`/api/user/teachers?${params.toString()}`);
    if (!data) return;
    if (Array.isArray(data)) {
      setTeachers(data);
      setTeacherTotal(data.length);
      return;
    }
    if (Array.isArray(data.data)) {
      setTeachers(data.data);
      setTeacherTotal(Number(data.total) || data.data.length);
    }
  };

  useEffect(() => {
    if (!isAdmin) return;
    loadSchools();
  }, [isAdmin]);

  useEffect(() => {
    if (!isAdmin) return;
    const delay = teacherSearch ? 300 : 0;
    const timeout = setTimeout(() => {
      loadTeachers();
    }, delay);
    return () => clearTimeout(timeout);
  }, [isAdmin, teacherPage, teacherLimit, teacherSortBy, teacherSortOrder, teacherSearch]);

  useEffect(() => {
    if (!editSchoolId) {
      setEditSchool({ name: '', code: '', school_emails: '' });
      return;
    }
    const school = schools.find((item) => item._id === editSchoolId);
    if (!school) return;
    setEditSchool({
      name: school.name || '',
      code: school.code || '',
      school_emails: Array.isArray(school.school_emails) ? school.school_emails.join(', ') : ''
    });
  }, [editSchoolId, schools]);

  useEffect(() => {
    if (!assignTeacherId) return;
    const teacher = teachers.find((item) => item._id === assignTeacherId);
    if (!teacher) return;
    if (teacher.school_id) {
      setAssignSchoolId(String(teacher.school_id));
    }
    if (teacher.branch_code) {
      setAssignBranchCode(String(teacher.branch_code));
    }
  }, [assignTeacherId, teachers]);

  useEffect(() => {
    if (!assignSchoolId) {
      setAssignBranchCode('');
      return;
    }
    const currentSchool = schools.find((item) => String(item._id) === String(assignSchoolId));
    const branchCodes = Array.isArray(currentSchool?.branches)
      ? currentSchool.branches.map((branch) => String(branch.code))
      : [];
    if (assignBranchCode && !branchCodes.includes(String(assignBranchCode))) {
      setAssignBranchCode('');
    }
  }, [assignSchoolId, schools, assignBranchCode]);

  useEffect(() => {
    setTeacherPage(1);
  }, [teacherSearch, teacherSortBy, teacherSortOrder, teacherLimit]);

  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil((teacherTotal || 0) / teacherLimit));
    if (teacherPage > totalPages) {
      setTeacherPage(totalPages);
    }
  }, [teacherPage, teacherTotal, teacherLimit]);

  const handleCreateSchool = async (event) => {
    event.preventDefault();
    setNotice(null);
    const schoolEmails = createSchool.school_emails
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);

    setLoading(true);
    const data = await apiRequest('/api/schools', {
      method: 'POST',
      body: JSON.stringify({
        name: createSchool.name,
        code: createSchool.code,
        school_emails: schoolEmails,
        database_name: createSchool.database_name,
        user_database_name: createSchool.user_database_name
      })
    });
    setLoading(false);
    if (data) {
      setNotice({ type: 'success', message: 'School created.' });
      setCreateSchool({
        name: '',
        code: '',
        school_emails: '',
        database_name: '',
        user_database_name: ''
      });
      loadSchools();
    }
  };

  const handleUpdateSchool = async (event) => {
    event.preventDefault();
    if (!editSchoolId) return;
    setNotice(null);
    const schoolEmails = editSchool.school_emails
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);

    setLoading(true);
    const data = await apiRequest(`/api/schools/${editSchoolId}`, {
      method: 'PATCH',
      body: JSON.stringify({
        name: editSchool.name,
        code: editSchool.code,
        school_emails: schoolEmails
      })
    });
    setLoading(false);
    if (data) {
      setNotice({ type: 'success', message: 'School updated.' });
      loadSchools();
    }
  };

  const handleAddBranch = async (event) => {
    event.preventDefault();
    if (!branchSchoolId) return;
    setNotice(null);
    setLoading(true);
    const data = await apiRequest('/api/schools/branche', {
      method: 'POST',
      body: JSON.stringify({
        name: branchForm.name,
        code: branchForm.code,
        school_id: branchSchoolId
      })
    });
    setLoading(false);
    if (data) {
      setNotice({ type: 'success', message: 'Branch added.' });
      setBranchForm({ name: '', code: '' });
      loadSchools();
    }
  };

  const handleAssignBranch = async (event) => {
    if (event?.preventDefault) {
      event.preventDefault();
    }
    if (!assignTeacherId || !assignBranchCode) return;
    setNotice(null);
    setLoading(true);
    const data = await apiRequest(`/api/user/admin/users/${assignTeacherId}/branch`, {
      method: 'PATCH',
      body: JSON.stringify({
        branch_code: assignBranchCode,
        school_id: assignSchoolId || undefined
      })
    });
    setLoading(false);
    if (data) {
      setNotice({ type: 'success', message: 'Teacher branch updated.' });
      loadTeachers();
      setAssignModalOpen(false);
    }
  };

  const handleCreateUser = async (event) => {
    event.preventDefault();
    setNotice(null);
    if (newUser.password !== newUser.confirm_password) {
      setNotice({ type: 'error', message: 'Password confirmation does not match.' });
      return;
    }
    setLoading(true);
    const data = await apiRequest('/api/user/admin/users', {
      method: 'POST',
      body: JSON.stringify({
        email: newUser.email,
        password: newUser.password,
        name: newUser.name,
        role: newUser.role,
        school_id: newUser.school_id,
        branch_code: newUser.role === 'teacher' ? newUser.branch_code : undefined
      })
    });
    setLoading(false);
    if (data) {
      setNotice({ type: 'success', message: 'User created.' });
      const createdSchool = schools.find((item) => item._id === newUser.school_id);
      const createdBranch = createdSchool?.branches?.find((branch) => branch.code === newUser.branch_code);
      setNewUserSummary({
        email: newUser.email,
        password: newUser.password,
        name: newUser.name || '',
        role: newUser.role,
        school: createdSchool?.name || '',
        branch: newUser.role === 'teacher' ? createdBranch?.name || '' : ''
      });
      setCopyNotice('');
      setNewUser({
        email: '',
        password: '',
        confirm_password: '',
        name: '',
        role: 'teacher',
        school_id: '',
        branch_code: ''
      });
      loadTeachers();
    }
  };

  const handleQuickAssign = (teacher) => {
    if (!teacher) return;
    setAssignTeacherId(teacher._id);
    if (teacher.school_id) {
      setAssignSchoolId(String(teacher.school_id));
    } else {
      setAssignSchoolId('');
    }
    if (teacher.branch_code) {
      setAssignBranchCode(String(teacher.branch_code));
    } else {
      setAssignBranchCode('');
    }
    setAssignModalOpen(true);
  };

  const handleOpenReset = (teacher) => {
    if (!teacher) return;
    setResetUserId(teacher._id);
    setResetPassword('');
    setResetPasswordConfirm('');
    setResetModalOpen(true);
    setAssignModalOpen(false);
  };

  const handleResetPassword = async () => {
    if (!resetUserId) return;
    if (!resetPassword) {
      setNotice({ type: 'error', message: 'Please enter a new password.' });
      return;
    }
    if (resetPassword !== resetPasswordConfirm) {
      setNotice({ type: 'error', message: 'Password confirmation does not match.' });
      return;
    }

    setResetLoading(true);
    const data = await apiRequest(`/api/user/admin/users/${resetUserId}/password`, {
      method: 'PATCH',
      body: JSON.stringify({
        password: resetPassword,
        confirm_password: resetPasswordConfirm
      })
    });
    setResetLoading(false);
    if (data) {
      setNotice({ type: 'success', message: 'Password reset successfully.' });
      setResetModalOpen(false);
      setResetUserId('');
      setResetPassword('');
      setResetPasswordConfirm('');
    }
  };

  const handleCopySummary = async () => {
    if (!newUserSummary) return;
    const lines = [
      `Email: ${newUserSummary.email}`,
      `Password: ${newUserSummary.password}`,
      `Role: ${newUserSummary.role}`,
      newUserSummary.name ? `Name: ${newUserSummary.name}` : null,
      newUserSummary.school ? `School: ${newUserSummary.school}` : null,
      newUserSummary.branch ? `Branch: ${newUserSummary.branch}` : null
    ].filter(Boolean);
    const text = lines.join('\n');
    try {
      await navigator.clipboard.writeText(text);
      setCopyNotice('Copied to clipboard.');
    } catch (error) {
      setCopyNotice('Copy failed. Please select and copy manually.');
    }
  };

  const handleSort = (column) => {
    if (teacherSortBy === column) {
      setTeacherSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setTeacherSortBy(column);
      setTeacherSortOrder('asc');
    }
  };


  if (!isAdmin) {
    return (
      <div className="pt-20 px-6">
        <h1 className="text-2xl font-bold">Admin Panel</h1>
        <p className="mt-4 text-red-500">Unauthorized</p>
      </div>
    );
  }

  const selectedEditSchool = schools.find((item) => item._id === editSchoolId);
  const branchSchool = schools.find((item) => item._id === branchSchoolId);
  const assignSchool = schools.find((item) => item._id === assignSchoolId);
  const newUserSchool = schools.find((item) => item._id === newUser.school_id);
  const selectedTeacher = teachers.find((teacher) => teacher._id === assignTeacherId);
  const resetTarget = teachers.find((teacher) => teacher._id === resetUserId);
  const assignBranches = Array.isArray(assignSchool?.branches) ? assignSchool.branches : [];
  const newUserBranches = Array.isArray(newUserSchool?.branches) ? newUserSchool.branches : [];
  const totalBranches = schools.reduce(
    (total, school) => total + (Array.isArray(school.branches) ? school.branches.length : 0),
    0
  );
  const stats = [
    { label: 'Schools', value: schools.length },
    { label: 'Branches', value: totalBranches },
    { label: 'Teachers', value: teacherTotal || teachers.length }
  ];
  const totalTeacherPages = Math.max(1, Math.ceil((teacherTotal || 0) / teacherLimit));
  const teacherStartIndex = teacherTotal === 0 ? 0 : (teacherPage - 1) * teacherLimit + 1;
  const teacherEndIndex = Math.min(teacherTotal, teacherPage * teacherLimit);

  return (
    <div className="pt-20 pb-10 h-screen overflow-auto bg-slate-50">
      <div className="mx-auto max-w-6xl px-6">
        <header className="flex flex-col gap-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-slate-400">Administration</p>
              <h1 className="text-3xl font-semibold text-slate-900">Admin Panel</h1>
              <p className="text-sm text-slate-500">Manage schools, branches, and staff accounts in one place.</p>
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              {stats.map((stat) => (
                <div key={stat.label} className="rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
                  <p className="text-xs uppercase tracking-wide text-slate-500">{stat.label}</p>
                  <p className="text-2xl font-semibold text-slate-900">{stat.value}</p>
                </div>
              ))}
            </div>
          </div>
          {notice && (
            <div
              className={`rounded-xl border px-4 py-3 text-sm ${
                notice.type === 'error'
                  ? 'border-rose-200 bg-rose-50 text-rose-700'
                  : 'border-emerald-200 bg-emerald-50 text-emerald-700'
              }`}
            >
              {notice.message}
            </div>
          )}
        </header>

        <div className="mt-6 flex flex-wrap items-center gap-2 text-sm">
          <span className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-400">Jump to</span>
          <a
            href="#section-schools"
            className="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-slate-600 transition hover:border-slate-300 hover:text-slate-900"
          >
            Schools
          </a>
          <a
            href="#section-branches"
            className="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-slate-600 transition hover:border-slate-300 hover:text-slate-900"
          >
            Branches
          </a>
          <a
            href="#section-teachers"
            className="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-slate-600 transition hover:border-slate-300 hover:text-slate-900"
          >
            Teachers
          </a>
          <a
            href="#section-users"
            className="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-slate-600 transition hover:border-slate-300 hover:text-slate-900"
          >
            Create User
          </a>
        </div>

        <section id="section-organization" className="mt-10">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-slate-400">Organization</p>
            <h2 className="text-xl font-semibold text-slate-900">Schools & Branches</h2>
            <p className="text-sm text-slate-500">Define schools first, then assign branches under each campus.</p>
          </div>

          <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
            <div id="section-schools" className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <div>
                <h3 className="text-lg font-semibold text-slate-900">Schools</h3>
                <p className="text-sm text-slate-500">Create or update a school profile.</p>
              </div>
              <div className="mt-6 grid gap-6">
                <form onSubmit={handleCreateSchool} className="rounded-xl border border-slate-200 bg-slate-50/60 p-4">
                  <div>
                    <h4 className="text-sm font-semibold text-slate-700">Create School</h4>
                    <p className={helperClassName}>Add a new school with optional database fields.</p>
                  </div>
                  <div className="mt-4 grid grid-cols-1 gap-3">
                    <div>
                      <label className={labelClassName}>School name</label>
                      <input
                        className={`${inputClassName} mt-1`}
                        placeholder="e.g. Bright Futures Academy"
                        value={createSchool.name}
                        onChange={(event) => setCreateSchool({ ...createSchool, name: event.target.value })}
                        required
                      />
                    </div>
                    <div>
                      <label className={labelClassName}>School code</label>
                      <input
                        className={`${inputClassName} mt-1`}
                        placeholder="e.g. BFA"
                        value={createSchool.code}
                        onChange={(event) => setCreateSchool({ ...createSchool, code: event.target.value })}
                        required
                      />
                    </div>
                    <div>
                      <label className={labelClassName}>Allowed email domains</label>
                      <input
                        className={`${inputClassName} mt-1`}
                        placeholder="domain.edu, school.org"
                        value={createSchool.school_emails}
                        onChange={(event) => setCreateSchool({ ...createSchool, school_emails: event.target.value })}
                      />
                    </div>
                    <div>
                      <label className={labelClassName}>Database name (optional)</label>
                      <input
                        className={`${inputClassName} mt-1`}
                        placeholder="education_prod"
                        value={createSchool.database_name}
                        onChange={(event) => setCreateSchool({ ...createSchool, database_name: event.target.value })}
                      />
                    </div>
                    <div>
                      <label className={labelClassName}>User database name (optional)</label>
                      <input
                        className={`${inputClassName} mt-1`}
                        placeholder="education_users"
                        value={createSchool.user_database_name}
                        onChange={(event) => setCreateSchool({ ...createSchool, user_database_name: event.target.value })}
                      />
                    </div>
                  </div>
                  <button
                    className={`${buttonBaseClass} mt-4 w-full bg-indigo-600 hover:bg-indigo-500`}
                    disabled={loading}
                  >
                    Create School
                  </button>
                </form>

                <form onSubmit={handleUpdateSchool} className="rounded-xl border border-slate-200 bg-slate-50/60 p-4">
                  <div>
                    <h4 className="text-sm font-semibold text-slate-700">Update School</h4>
                    <p className={helperClassName}>Select a school to edit its details.</p>
                  </div>
                  <div className="mt-4 grid grid-cols-1 gap-3">
                    <div>
                      <label className={labelClassName}>Select school</label>
                      <select
                        className={`${inputClassName} mt-1`}
                        value={editSchoolId}
                        onChange={(event) => setEditSchoolId(event.target.value)}
                      >
                        <option value="">Select school</option>
                        {schools.map((school) => (
                          <option key={school._id} value={school._id}>
                            {school.name} ({school.code})
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className={labelClassName}>School name</label>
                      <input
                        className={`${inputClassName} mt-1`}
                        placeholder="Name"
                        value={editSchool.name}
                        onChange={(event) => setEditSchool({ ...editSchool, name: event.target.value })}
                        disabled={!selectedEditSchool}
                      />
                    </div>
                    <div>
                      <label className={labelClassName}>School code</label>
                      <input
                        className={`${inputClassName} mt-1`}
                        placeholder="Code"
                        value={editSchool.code}
                        onChange={(event) => setEditSchool({ ...editSchool, code: event.target.value })}
                        disabled={!selectedEditSchool}
                      />
                    </div>
                    <div>
                      <label className={labelClassName}>Allowed email domains</label>
                      <input
                        className={`${inputClassName} mt-1`}
                        placeholder="domain.edu"
                        value={editSchool.school_emails}
                        onChange={(event) => setEditSchool({ ...editSchool, school_emails: event.target.value })}
                        disabled={!selectedEditSchool}
                      />
                    </div>
                  </div>
                  <button
                    className={`${buttonBaseClass} mt-4 w-full bg-slate-900 hover:bg-slate-800`}
                    disabled={!selectedEditSchool || loading}
                  >
                    Update School
                  </button>
                </form>
              </div>
            </div>

            <div id="section-branches" className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <div>
                <h3 className="text-lg font-semibold text-slate-900">Branches</h3>
                <p className="text-sm text-slate-500">Manage branch lists for each school.</p>
              </div>
              <div className="mt-5 grid gap-4">
                <div>
                  <label className={labelClassName}>Select school</label>
                  <select
                    className={`${inputClassName} mt-1`}
                    value={branchSchoolId}
                    onChange={(event) => setBranchSchoolId(event.target.value)}
                  >
                    <option value="">Select school</option>
                    {schools.map((school) => (
                      <option key={school._id} value={school._id}>
                        {school.name} ({school.code})
                      </option>
                    ))}
                  </select>
                </div>

                <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 p-4">
                  <p className="text-xs uppercase tracking-wide text-slate-500">Current branches</p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {branchSchool && Array.isArray(branchSchool.branches) && branchSchool.branches.length > 0 ? (
                      branchSchool.branches.map((branch) => (
                        <span
                          key={branch.code}
                          className="rounded-full bg-white px-3 py-1 text-xs font-medium text-slate-600 shadow-sm"
                        >
                          {branch.name} ({branch.code})
                        </span>
                      ))
                    ) : (
                      <span className="text-sm text-slate-500">Select a school to see branches.</span>
                    )}
                  </div>
                </div>

                <form onSubmit={handleAddBranch} className="grid grid-cols-1 gap-3">
                  <div>
                    <label className={labelClassName}>Branch name</label>
                    <input
                      className={`${inputClassName} mt-1`}
                      placeholder="e.g. Downtown"
                      value={branchForm.name}
                      onChange={(event) => setBranchForm({ ...branchForm, name: event.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <label className={labelClassName}>Branch code</label>
                    <input
                      className={`${inputClassName} mt-1`}
                      placeholder="DT"
                      value={branchForm.code}
                      onChange={(event) => setBranchForm({ ...branchForm, code: event.target.value })}
                      required
                    />
                  </div>
                  <button
                    className={`${buttonBaseClass} w-full bg-blue-600 hover:bg-blue-500`}
                    disabled={!branchSchoolId || loading}
                  >
                    Add Branch
                  </button>
                </form>
              </div>
            </div>
          </div>
        </section>

        <section id="section-people" className="mt-10">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-slate-400">People</p>
            <h2 className="text-xl font-semibold text-slate-900">Teachers & Accounts</h2>
            <p className="text-sm text-slate-500">Search staff and update branch assignments quickly.</p>
          </div>

          <div id="section-teachers" className="mt-6 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
              <div>
                <h3 className="text-lg font-semibold text-slate-900">Teachers</h3>
                <p className="text-sm text-slate-500">Search, review, and assign teachers to branches.</p>
              </div>
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                <input
                  className={`${inputClassName} w-full sm:w-64`}
                  placeholder="Search by name, email, school, branch"
                  value={teacherSearch}
                  onChange={(event) => setTeacherSearch(event.target.value)}
                />
                {teacherSearch && (
                  <button
                    type="button"
                    className="rounded-lg border border-slate-200 px-3 py-2 text-sm font-medium text-slate-600 hover:border-slate-300 hover:text-slate-900"
                    onClick={() => setTeacherSearch('')}
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>

            <div className="mt-6 grid grid-cols-1 gap-6">
              <div className="rounded-xl border border-slate-200 bg-slate-50/70 px-4 py-3 text-sm text-slate-600">
                Click <span className="font-semibold text-slate-700">Assign</span> on a teacher row to open the branch
                editor.
              </div>

              <div className="hidden overflow-auto rounded-xl border border-slate-200 lg:block">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                    <tr>
                      <th className="px-4 py-3 text-center">No.</th>
                      <th className="px-4 py-3">
                        <button
                          type="button"
                          className="inline-flex items-center gap-1 font-semibold text-slate-500 hover:text-slate-700"
                          onClick={() => handleSort('name')}
                        >
                          Name
                          {teacherSortBy === 'name' && (
                            <span className="text-[10px] text-slate-400">
                              {teacherSortOrder === 'asc' ? '↑' : '↓'}
                            </span>
                          )}
                        </button>
                      </th>
                      <th className="px-4 py-3">
                        <button
                          type="button"
                          className="inline-flex items-center gap-1 font-semibold text-slate-500 hover:text-slate-700"
                          onClick={() => handleSort('email')}
                        >
                          Email
                          {teacherSortBy === 'email' && (
                            <span className="text-[10px] text-slate-400">
                              {teacherSortOrder === 'asc' ? '↑' : '↓'}
                            </span>
                          )}
                        </button>
                      </th>
                      <th className="px-4 py-3">
                        <button
                          type="button"
                          className="inline-flex items-center gap-1 font-semibold text-slate-500 hover:text-slate-700"
                          onClick={() => handleSort('school')}
                        >
                          School
                          {teacherSortBy === 'school' && (
                            <span className="text-[10px] text-slate-400">
                              {teacherSortOrder === 'asc' ? '↑' : '↓'}
                            </span>
                          )}
                        </button>
                      </th>
                      <th className="px-4 py-3">
                        <button
                          type="button"
                          className="inline-flex items-center gap-1 font-semibold text-slate-500 hover:text-slate-700"
                          onClick={() => handleSort('branch')}
                        >
                          Branch
                          {teacherSortBy === 'branch' && (
                            <span className="text-[10px] text-slate-400">
                              {teacherSortOrder === 'asc' ? '↑' : '↓'}
                            </span>
                          )}
                        </button>
                      </th>
                      <th className="px-4 py-3">
                        <button
                          type="button"
                          className="inline-flex items-center gap-1 font-semibold text-slate-500 hover:text-slate-700"
                          onClick={() => handleSort('branch_code')}
                        >
                          Code
                          {teacherSortBy === 'branch_code' && (
                            <span className="text-[10px] text-slate-400">
                              {teacherSortOrder === 'asc' ? '↑' : '↓'}
                            </span>
                          )}
                        </button>
                      </th>
                      <th className="px-4 py-3">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {teachers.map((teacher, index) => {
                      const isSelected = String(assignTeacherId) === String(teacher._id);
                      const rowNumber = teacherStartIndex + index;
                      return (
                        <tr
                          key={teacher._id}
                          className={`hover:bg-slate-50/70 ${isSelected ? 'bg-indigo-50/60' : ''}`}
                        >
                          <td className="px-4 py-3 text-center text-slate-500">{rowNumber}</td>
                          <td className="px-4 py-3 text-slate-900">{teacher.name || 'Unnamed'}</td>
                          <td className="px-4 py-3 text-slate-600">{teacher.email}</td>
                          <td className="px-4 py-3 text-slate-600">{teacher.school || '-'}</td>
                          <td className="px-4 py-3 text-slate-600">{teacher.branch || '-'}</td>
                          <td className="px-4 py-3 text-slate-600">{teacher.branch_code || '-'}</td>
                          <td className="px-4 py-3">
                            <div className="flex flex-wrap items-center gap-2">
                              <button
                                type="button"
                                className="rounded-lg border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-600 hover:border-slate-300 hover:text-slate-900"
                                onClick={() => handleQuickAssign(teacher)}
                              >
                                Assign
                              </button>
                              <button
                                type="button"
                                className="rounded-lg border border-rose-200 px-3 py-1 text-xs font-semibold text-rose-600 hover:border-rose-300 hover:text-rose-700"
                                onClick={() => handleOpenReset(teacher)}
                              >
                                Reset Password
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                    {teachers.length === 0 && (
                      <tr>
                        <td colSpan={7} className="px-4 py-4 text-center text-slate-500">
                          {teacherSearch ? 'No teachers match the current search.' : 'No teachers found.'}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div className="grid gap-3 lg:hidden">
                {teachers.map((teacher, index) => {
                  const isSelected = String(assignTeacherId) === String(teacher._id);
                  const rowNumber = teacherStartIndex + index;
                  return (
                    <div
                      key={teacher._id}
                      className={`rounded-xl border border-slate-200 bg-white p-4 shadow-sm ${
                        isSelected ? 'ring-2 ring-indigo-200' : ''
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="text-[10px] uppercase tracking-wide text-slate-400">No. {rowNumber}</p>
                          <p className="text-sm font-semibold text-slate-900">{teacher.name || 'Unnamed'}</p>
                          <p className="text-xs text-slate-500">{teacher.email}</p>
                        </div>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          className="rounded-lg border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-600"
                          onClick={() => handleQuickAssign(teacher)}
                        >
                          Assign
                        </button>
                        <button
                          type="button"
                          className="rounded-lg border border-rose-200 px-3 py-1 text-xs font-semibold text-rose-600"
                          onClick={() => handleOpenReset(teacher)}
                        >
                          Reset
                        </button>
                      </div>
                      </div>
                      <div className="mt-3 grid grid-cols-2 gap-2 text-xs text-slate-600">
                        <div>
                          <p className="text-[10px] uppercase tracking-wide text-slate-400">School</p>
                          <p>{teacher.school || '-'}</p>
                        </div>
                        <div>
                          <p className="text-[10px] uppercase tracking-wide text-slate-400">Branch</p>
                          <p>{teacher.branch || '-'}</p>
                        </div>
                        <div>
                          <p className="text-[10px] uppercase tracking-wide text-slate-400">Code</p>
                          <p>{teacher.branch_code || '-'}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
                {teachers.length === 0 && (
                  <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 p-4 text-center text-sm text-slate-500">
                    {teacherSearch ? 'No teachers match the current search.' : 'No teachers found.'}
                  </div>
                )}
              </div>

              <div className="flex flex-col gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-600 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  {teacherTotal === 0 ? (
                    <span>Showing 0 of 0 teachers</span>
                  ) : (
                    <span>
                      Showing {teacherStartIndex}-{teacherEndIndex} of {teacherTotal} teachers
                    </span>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <div className="flex items-center gap-2 text-xs text-slate-500">
                    <span>Rows per page</span>
                    <select
                      className="rounded-lg border border-slate-200 bg-white px-2 py-1 text-xs text-slate-600"
                      value={teacherLimit}
                      onChange={(event) => setTeacherLimit(Number(event.target.value))}
                    >
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      className="rounded-lg border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-600 hover:border-slate-300 hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-60"
                      onClick={() => setTeacherPage((prev) => Math.max(prev - 1, 1))}
                      disabled={teacherPage <= 1}
                    >
                      Prev
                    </button>
                    <span className="text-xs text-slate-500">
                      Page {teacherPage} of {totalTeacherPages}
                    </span>
                    <button
                      type="button"
                      className="rounded-lg border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-600 hover:border-slate-300 hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-60"
                      onClick={() => setTeacherPage((prev) => Math.min(prev + 1, totalTeacherPages))}
                      disabled={teacherPage >= totalTeacherPages}
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div id="section-users" className="mt-6 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <div>
              <h3 className="text-lg font-semibold text-slate-900">Create User</h3>
              <p className="text-sm text-slate-500">Create new staff accounts and assign them immediately.</p>
            </div>
            <form onSubmit={handleCreateUser} className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
              <div>
                <label className={labelClassName}>Email</label>
                <input
                  className={`${inputClassName} mt-1`}
                  placeholder="name@school.org"
                  value={newUser.email}
                  onChange={(event) => setNewUser({ ...newUser, email: event.target.value })}
                  required
                />
              </div>
              <div>
                <label className={labelClassName}>Password</label>
                <input
                  className={`${inputClassName} mt-1`}
                  placeholder="Temporary password"
                  type="password"
                  value={newUser.password}
                  onChange={(event) => setNewUser({ ...newUser, password: event.target.value })}
                  required
                />
              </div>
              <div>
                <label className={labelClassName}>Confirm password</label>
                <input
                  className={`${inputClassName} mt-1`}
                  placeholder="Re-enter password"
                  type="password"
                  value={newUser.confirm_password}
                  onChange={(event) => setNewUser({ ...newUser, confirm_password: event.target.value })}
                  required
                />
              </div>
              <div>
                <label className={labelClassName}>Name</label>
                <input
                  className={`${inputClassName} mt-1`}
                  placeholder="Optional display name"
                  value={newUser.name}
                  onChange={(event) => setNewUser({ ...newUser, name: event.target.value })}
                />
              </div>
              <div>
                <label className={labelClassName}>Role</label>
                <select
                  className={`${inputClassName} mt-1`}
                  value={newUser.role}
                  onChange={(event) => {
                    const role = event.target.value;
                    setNewUser({
                      ...newUser,
                      role,
                      branch_code: role === 'teacher' ? newUser.branch_code : ''
                    });
                  }}
                >
                  <option value="teacher">Teacher</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              <div>
                <label className={labelClassName}>School</label>
                <select
                  className={`${inputClassName} mt-1`}
                  value={newUser.school_id}
                  onChange={(event) => setNewUser({ ...newUser, school_id: event.target.value, branch_code: '' })}
                >
                  <option value="">Select school</option>
                  {schools.map((school) => (
                    <option key={school._id} value={school._id}>
                      {school.name} ({school.code})
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelClassName}>Branch</label>
                <select
                  className={`${inputClassName} mt-1`}
                  value={newUser.branch_code}
                  onChange={(event) => setNewUser({ ...newUser, branch_code: event.target.value })}
                  disabled={newUser.role !== 'teacher' || !newUser.school_id}
                  required={newUser.role === 'teacher'}
                >
                  <option value="">Select branch</option>
                  {newUserBranches.map((branch) => (
                    <option key={branch.code} value={branch.code}>
                      {branch.name} ({branch.code})
                    </option>
                  ))}
                </select>
              </div>
              <button
                className={`${buttonBaseClass} md:col-span-2 lg:col-span-3 bg-emerald-600 hover:bg-emerald-500`}
                disabled={loading}
              >
                Create User
              </button>
            </form>
            {newUserSummary && (
              <div className="mt-6 rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <h4 className="text-sm font-semibold text-slate-800">User Created</h4>
                    <p className="text-xs text-slate-500">Copy these details and send them to the user.</p>
                  </div>
                  <button
                    type="button"
                    className="rounded-lg border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-600 hover:border-slate-300 hover:text-slate-900"
                    onClick={handleCopySummary}
                  >
                    Copy to clipboard
                  </button>
                </div>
                <div className="mt-3 grid gap-2 text-xs text-slate-600">
                  <span>
                    <strong>Email:</strong> {newUserSummary.email}
                  </span>
                  <span>
                    <strong>Password:</strong> {newUserSummary.password}
                  </span>
                  <span>
                    <strong>Role:</strong> {newUserSummary.role}
                  </span>
                  {newUserSummary.name && (
                    <span>
                      <strong>Name:</strong> {newUserSummary.name}
                    </span>
                  )}
                  {newUserSummary.school && (
                    <span>
                      <strong>School:</strong> {newUserSummary.school}
                    </span>
                  )}
                  {newUserSummary.branch && (
                    <span>
                      <strong>Branch:</strong> {newUserSummary.branch}
                    </span>
                  )}
                  {copyNotice && <span className="text-xs text-emerald-600">{copyNotice}</span>}
                </div>
              </div>
            )}
          </div>
        </section>
      </div>
      {assignModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 px-4 py-6"
          onClick={() => setAssignModalOpen(false)}
        >
          <div
            className="w-full max-w-2xl rounded-2xl bg-white shadow-xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
              <div>
                <p className="text-xs uppercase tracking-[0.3em] text-slate-400">Assign Branch</p>
                <h3 className="text-lg font-semibold text-slate-900">Manage Teacher Branch</h3>
              </div>
              <button
                type="button"
                className="rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-600 hover:border-slate-300 hover:text-slate-900"
                onClick={() => setAssignModalOpen(false)}
              >
                Close
              </button>
            </div>
            <div className="space-y-6 px-6 py-5">
              <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
                {selectedTeacher ? (
                  <div className="flex flex-col gap-1">
                    <span className="font-semibold text-slate-800">
                      {selectedTeacher.name || 'Unnamed'} · {selectedTeacher.email}
                    </span>
                    <span>
                      Current: {selectedTeacher.school || 'No school'} · {selectedTeacher.branch || 'No branch'}
                    </span>
                  </div>
                ) : (
                  <span>Select a teacher to begin assignment.</span>
                )}
              </div>

              <div className="grid gap-4 md:grid-cols-3">
                <div>
                  <label className={labelClassName}>Teacher</label>
                  <select
                    className={`${inputClassName} mt-1`}
                    value={assignTeacherId}
                    onChange={(event) => setAssignTeacherId(event.target.value)}
                  >
                    <option value="">Select teacher</option>
                    {teachers.map((teacher) => (
                      <option key={teacher._id} value={teacher._id}>
                        {teacher.name} ({teacher.email})
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={labelClassName}>School</label>
                  <select
                    className={`${inputClassName} mt-1`}
                    value={assignSchoolId}
                    onChange={(event) => setAssignSchoolId(event.target.value)}
                  >
                    <option value="">Select school</option>
                    {schools.map((school) => (
                      <option key={school._id} value={school._id}>
                        {school.name} ({school.code})
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={labelClassName}>Branch</label>
                  <select
                    className={`${inputClassName} mt-1`}
                    value={assignBranchCode}
                    onChange={(event) => setAssignBranchCode(event.target.value)}
                    disabled={!assignSchoolId}
                  >
                    <option value="">Select branch</option>
                    {assignBranches.map((branch) => (
                      <option key={branch.code} value={branch.code}>
                        {branch.name} ({branch.code})
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
            <div className="flex flex-col gap-2 border-t border-slate-200 px-6 py-4 sm:flex-row sm:justify-end">
              <button
                type="button"
                className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 hover:border-slate-300 hover:text-slate-900"
                onClick={() => setAssignModalOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className={`${buttonBaseClass} bg-indigo-600 hover:bg-indigo-500`}
                disabled={!assignTeacherId || !assignBranchCode || loading}
                onClick={handleAssignBranch}
              >
                Save Assignment
              </button>
            </div>
          </div>
        </div>
      )}
      {resetModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 px-4 py-6"
          onClick={() => setResetModalOpen(false)}
        >
          <div
            className="w-full max-w-xl rounded-2xl bg-white shadow-xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
              <div>
                <p className="text-xs uppercase tracking-[0.3em] text-slate-400">Reset Password</p>
                <h3 className="text-lg font-semibold text-slate-900">Set a New Password</h3>
              </div>
              <button
                type="button"
                className="rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-600 hover:border-slate-300 hover:text-slate-900"
                onClick={() => setResetModalOpen(false)}
              >
                Close
              </button>
            </div>
            <div className="space-y-4 px-6 py-5">
              <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
                {resetTarget ? (
                  <div className="flex flex-col gap-1">
                    <span className="font-semibold text-slate-800">
                      {resetTarget.name || 'Unnamed'} · {resetTarget.email}
                    </span>
                    <span>
                      School: {resetTarget.school || 'No school'} · Branch: {resetTarget.branch || 'No branch'}
                    </span>
                  </div>
                ) : (
                  <span>Select a teacher to reset their password.</span>
                )}
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <label className={labelClassName}>New password</label>
                  <input
                    className={`${inputClassName} mt-1`}
                    type="password"
                    value={resetPassword}
                    onChange={(event) => setResetPassword(event.target.value)}
                    placeholder="Enter new password"
                  />
                </div>
                <div>
                  <label className={labelClassName}>Confirm password</label>
                  <input
                    className={`${inputClassName} mt-1`}
                    type="password"
                    value={resetPasswordConfirm}
                    onChange={(event) => setResetPasswordConfirm(event.target.value)}
                    placeholder="Re-enter new password"
                  />
                </div>
              </div>
            </div>
            <div className="flex flex-col gap-2 border-t border-slate-200 px-6 py-4 sm:flex-row sm:justify-end">
              <button
                type="button"
                className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 hover:border-slate-300 hover:text-slate-900"
                onClick={() => setResetModalOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className={`${buttonBaseClass} bg-rose-600 hover:bg-rose-500`}
                disabled={!resetUserId || !resetPassword || !resetPasswordConfirm || resetLoading}
                onClick={handleResetPassword}
              >
                {resetLoading ? 'Saving...' : 'Save Password'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPanel;
